import { type ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogContentText from '@mui/material/DialogContentText'
import FormControlLabel from '@mui/material/FormControlLabel'

import useSuppressFlag from 'global/hooks/useSuppressFlag'
import KNDialog from './KNDialog'
import type { KNConfirmationDialogProps } from './types'

const KNConfirmationDialog = ({
  message,
  continueLabel,
  skipConfirmationFlag,
  open,
  onAction,
  onClose,
}: KNConfirmationDialogProps): ReactElement => {
  const { t } = useTranslation()
  const [skipConfirmationActive, setSkipConfirmationActive] = useState(false)
  const { suppressIndefinitely } = useSuppressFlag(skipConfirmationFlag)

  const handleConfirmation = useCallback(() => {
    if (skipConfirmationFlag && skipConfirmationActive) {
      suppressIndefinitely()
    }
    onAction()
  }, [skipConfirmationFlag, skipConfirmationActive])

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          onClick={handleConfirmation}
        >
          {continueLabel ?? t('general.continue')}
        </Button>
      }
      maxWidth="xs"
    >
      <DialogContentText component="div">{message}</DialogContentText>
      {skipConfirmationFlag && (
        <DialogContentText component="div" mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                value={skipConfirmationActive}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  setSkipConfirmationActive(event.target.checked || false)
                }
                sx={{
                  py: 0,
                }}
              />
            }
            label={t('general.dont_show_again')}
          />
        </DialogContentText>
      )}
    </KNDialog>
  )
}

export default KNConfirmationDialog
